import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AdminToast from '../admin/AdminToast';
import AcademiaSelect from '../common/AcademiaSelect';
import FormateurPageShell from './FormateurPageShell';
import FormateurProfilExtrasEditor from './FormateurProfilExtrasEditor';
import UserProfileAvatar from '../common/UserProfileAvatar';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import {
  parseDiplomes,
  parseDisponibilites,
  parseReferencements,
  type FormateurDiplome,
  type FormateurDisponibilite,
  type FormateurReferencement,
} from '../../types/formateurProfilExtras';
import { resolveFormateurSpecialiteOptions } from '../../constants/formateurSpecialites';
import { authService } from '../../services/authService';
import {
  addFormateurDocument,
  fetchFormateurDocuments,
  fetchFormateurProfil,
  patchFormateurProfil,
  uploadFormateurCv,
  uploadProfilePhoto,
} from '../../services/academiaFormateurService';

type FormateurUser = {
  telephone?: string;
  email?: string;
  profil?: { prenom?: string; nom?: string; photoUrl?: string | null };
};

type ProfilData = {
  utilisateur?: { telephone?: string; email?: string };
  profil?: { prenom?: string; nom?: string; urlAvatar?: string | null };
  profilFormateur?: {
    specialite?: string;
    experienceAnnees?: number;
    urlCv?: string;
    biographie?: string;
    profilComplet?: boolean;
    diplomes?: unknown;
    disponibilites?: unknown;
    referencements?: unknown;
  };
  evaluations?: {
    id: string;
    notePonctualite: number;
    notePedagogie: number;
    commentaire?: string | null;
    dateCreation: string;
    apprenantLabel?: string;
    cohorteCode?: string | null;
  }[];
  indicateursOperationnels?: {
    seancesPassees: number;
    seancesAvecPresencesSaisies: number;
    tauxSaisiePresencesPct: number | null;
    nombrePrestations?: number;
    heuresPresteesTotal?: number;
    heuresPresteesMois?: number;
    remunerationsDuCdf?: number;
    montantPayeCdf?: number;
  };
  performance?: {
    noteMoyenne?: number;
    notePonctualiteMoyenne?: number;
    notePedagogieMoyenne?: number;
    tauxSatisfactionPct?: number | null;
  };
};

interface FormateurProfilPanelProps {
  user: FormateurUser;
  onboarding?: boolean;
  mustChangePassword?: boolean;
  onProfileSaved?: () => void | Promise<void>;
  onPasswordChanged?: () => void | Promise<void>;
}

type ToastState = { message: string; variant: 'success' | 'error' } | null;

function formatDocStatut(statut: string) {
  switch (statut) {
    case 'VALIDE':
      return 'Validé';
    case 'REJETE':
      return 'Rejeté';
    case 'EN_ATTENTE':
      return 'En attente';
    default:
      return statut;
  }
}

function docStatutTone(statut: string): 'success' | 'warn' | 'danger' {
  if (statut === 'VALIDE') return 'success';
  if (statut === 'REJETE') return 'danger';
  return 'warn';
}

export default function FormateurProfilPanel({
  user,
  onboarding = false,
  mustChangePassword = false,
  onProfileSaved,
  onPasswordChanged,
}: FormateurProfilPanelProps) {
  const navigate = useNavigate();
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState('');
  const [profilData, setProfilData] = useState<ProfilData | null>(null);
  const [form, setForm] = useState({ specialite: '', urlCv: '', biographie: '', experienceAnnees: '0' });
  const [photoUrl, setPhotoUrl] = useState(user.profil?.photoUrl ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);
  const [loadError, setLoadError] = useState('');

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);
  const [ancienMotdepasse, setAncienMotdepasse] = useState('');
  const [nouveauMotdepasse, setNouveauMotdepasse] = useState('');
  const [confirmMotdepasse, setConfirmMotdepasse] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [diplomes, setDiplomes] = useState<FormateurDiplome[]>([]);
  const [disponibilites, setDisponibilites] = useState<FormateurDisponibilite[]>([]);
  const [referencements, setReferencements] = useState<FormateurReferencement[]>([]);
  const [documents, setDocuments] = useState<{ id: string; typeDocument: string; statutValidation: string }[]>([]);

  const specialiteOptions = useMemo(
    () => resolveFormateurSpecialiteOptions(form.specialite),
    [form.specialite],
  );

  const loadProfil = useCallback(async () => {
    setLoadError('');
    try {
      const d = (await fetchFormateurProfil()) as ProfilData;
      setProfilData(d);
      const pf = d?.profilFormateur;
      setForm({
        specialite: pf?.specialite ?? '',
        urlCv: pf?.urlCv ?? '',
        biographie: pf?.biographie ?? '',
        experienceAnnees: String(pf?.experienceAnnees ?? 0),
      });
      setDiplomes(parseDiplomes(pf?.diplomes));
      setDisponibilites(parseDisponibilites(pf?.disponibilites));
      setReferencements(parseReferencements(pf?.referencements));
      setDocuments(await fetchFormateurDocuments());
      const avatar = d?.profil?.urlAvatar ?? user.profil?.photoUrl ?? '';
      setPhotoUrl(avatar || '');
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Impossible de charger le profil');
    }
  }, [user.profil?.photoUrl]);

  useEffect(() => {
    void loadProfil();
  }, [loadProfil]);

  useEffect(() => {
    if (!photoFile) {
      setPhotoPreviewUrl('');
      return undefined;
    }
    const nextUrl = URL.createObjectURL(photoFile);
    setPhotoPreviewUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [photoFile]);

  const prenom = profilData?.profil?.prenom ?? user.profil?.prenom ?? '';
  const nom = profilData?.profil?.nom ?? user.profil?.nom ?? '';
  const displayName = [prenom, nom].filter(Boolean).join(' ') || user.telephone || 'Formateur';
  const telephone = profilData?.utilisateur?.telephone ?? user.telephone;
  const email = profilData?.utilisateur?.email ?? user.email;

  const perf = profilData?.performance;
  const indicateurs = profilData?.indicateursOperationnels;
  const evaluations = profilData?.evaluations ?? [];
  const noteGlobale =
    perf?.noteMoyenne != null && perf.noteMoyenne > 0
      ? `${perf.noteMoyenne.toFixed(1)}/5`
      : '—';

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    setToast(null);
    try {
      let urlCv = form.urlCv || undefined;
      let nextPhotoUrl: string | undefined;
      if (photoFile) {
        setIsUploadingPhoto(true);
        nextPhotoUrl = await uploadProfilePhoto(photoFile);
        setPhotoUrl(nextPhotoUrl);
        setIsUploadingPhoto(false);
      }
      if (cvFile) urlCv = await uploadFormateurCv(cvFile);
      if (cvFile && urlCv) {
        await addFormateurDocument({ typeDocument: 'FORMATEUR_CV', urlFichier: urlCv });
      }
      await patchFormateurProfil({
        specialite: form.specialite,
        urlCv,
        biographie: form.biographie,
        experienceAnnees: Number(form.experienceAnnees),
        diplomes: diplomes.filter((d) => d.libelle.trim()),
        disponibilites: disponibilites.filter((d) => d.jour.trim()),
        referencements: referencements.filter((r) => r.nom.trim()),
        ...(nextPhotoUrl ? { photoUrl: nextPhotoUrl } : {}),
      });
      if (nextPhotoUrl) {
        const u = authService.getStoredAuth().user;
        if (u?.profil) {
          u.profil.photoUrl = nextPhotoUrl;
          localStorage.setItem('onego_user', JSON.stringify(u));
        }
      }
      if (urlCv) setForm((f) => ({ ...f, urlCv }));
      setPhotoFile(null);
      setCvFile(null);
      showToast(
        onboarding
          ? 'Profil enregistré. Définissez votre mot de passe personnel dans la section ci-dessous.'
          : 'Profil enregistré avec succès.',
        'success',
      );
      await onProfileSaved?.();
      await loadProfil();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Échec enregistrement', 'error');
    } finally {
      setIsSaving(false);
      setIsUploadingPhoto(false);
    }
  };

  const handleChangePassword = async () => {
    if (isChangingPassword) return;
    setToast(null);

    const notifyPasswordError = (msg: string) => showToast(msg, 'error');

    if (!ancienMotdepasse || !nouveauMotdepasse || !confirmMotdepasse) {
      notifyPasswordError('Tous les champs mot de passe sont requis.');
      return;
    }
    if (nouveauMotdepasse.length < 8) {
      notifyPasswordError('Le nouveau mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (nouveauMotdepasse !== confirmMotdepasse) {
      notifyPasswordError('La confirmation ne correspond pas au nouveau mot de passe.');
      return;
    }

    setIsChangingPassword(true);
    try {
      await authService.changePassword({
        ancienMotdepasse,
        nouveauMotdepasse,
        confirmMotdepasse,
      });
      showToast('Mot de passe mis à jour avec succès.', 'success');
      setAncienMotdepasse('');
      setNouveauMotdepasse('');
      setConfirmMotdepasse('');
      await onPasswordChanged?.();
      if (mustChangePassword) {
        navigate('/formateur');
      }
    } catch (e) {
      notifyPasswordError(e instanceof Error ? e.message : 'Changement de mot de passe impossible.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const passwordOnboarding = mustChangePassword || onboarding;

  return (
    <FormateurPageShell
      title="Mon profil"
      description={
        onboarding
          ? 'Complétez votre profil pour accéder à l’espace formateur, puis définissez votre mot de passe personnel.'
          : 'Présentez votre expertise, votre parcours et vos documents aux apprenants et à l’administration.'
      }
      isLoading={!profilData && !loadError}
      isError={Boolean(loadError)}
      errorMessage={loadError}
      onRetry={() => void loadProfil()}
      sideCard={{
        label: 'Note globale',
        value: noteGlobale,
        helper: form.specialite.trim() || displayName,
        sideLinks: [
          { label: 'Rémunération', to: '/formateur/remuneration', icon: 'payments' },
          { label: 'Impact', to: '/formateur/performance', icon: 'school' },
        ],
      }}
      primaryAction={
        onboarding ? (
          <span className="inline-flex items-center gap-2 rounded-lg bg-white/20 px-3 py-1.5 text-xs font-bold text-white backdrop-blur-md">
            <LucideSymbolIcon name="info" tone="inherit" className="text-white" size={14} />
            Étape 1 sur 2 — profil
          </span>
        ) : undefined
      }
    >
      <AdminToast
        message={toast?.message ?? null}
        variant={toast?.variant ?? 'success'}
        onClose={dismissToast}
      />

      <div className="formateur-profil">
        {onboarding ? (
          <div className="formateur-profil-onboarding" role="status">
            <LucideSymbolIcon name="info" tone="inherit" size={20} className="shrink-0 text-amber-800" />
            <div>
              <p className="font-bold">Bienvenue sur JCONSULT MY Academia</p>
              <p className="mt-1">
                Renseignez votre spécialité, expérience et biographie, enregistrez, puis choisissez votre mot de passe
                personnel dans la section « Sécurité du compte ».
              </p>
            </div>
          </div>
        ) : null}

        <div className="formateur-profil__grid">
          <section className="formateur-profil-panel" aria-labelledby="formateur-profil-identity-heading">
            <header className="formateur-profil-panel__header">
              <span className="formateur-profil-panel__header-icon" aria-hidden>
                <LucideSymbolIcon name="person" tone="inherit" size={22} />
              </span>
              <div className="min-w-0">
                <h2 id="formateur-profil-identity-heading" className="formateur-profil-panel__title">
                  Identité
                </h2>
                <p className="formateur-profil-panel__subtitle">Photo, coordonnées et documents</p>
              </div>
            </header>
            <div className="formateur-profil-panel__body">
              <div className="formateur-profil-identity__hero">
                <UserProfileAvatar
                  name={displayName}
                  prenom={prenom}
                  nom={nom}
                  photoUrl={photoUrl || null}
                  size="md"
                />
                <div className="min-w-0 flex-1">
                  <p className="formateur-profil-identity__name">{displayName}</p>
                  <label className="formateur-profil__btn formateur-profil__btn--outline mt-2">
                    <LucideSymbolIcon name="upload" tone="inherit" size={16} />
                    {isUploadingPhoto ? 'Téléversement…' : 'Changer la photo'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
                    />
                  </label>
                  {photoFile ? (
                    <p className="formateur-profil-identity__hint">Nouvelle photo : {photoFile.name}</p>
                  ) : null}
                  {photoPreviewUrl ? (
                    <div className="formateur-profil-identity__preview">
                      <p className="formateur-profil-identity__hint">Prévisualisation</p>
                      <img src={photoPreviewUrl} alt="" className="formateur-profil-identity__preview-img" />
                    </div>
                  ) : null}
                </div>
              </div>

              <ul className="formateur-profil-facts">
                <li className="formateur-profil-fact">
                  <span className="formateur-profil-fact__icon" aria-hidden>
                    <LucideSymbolIcon name="badge" tone="inherit" size={18} />
                  </span>
                  <div className="min-w-0">
                    <p className="formateur-profil-fact__label">Nom affiché</p>
                    <p className="formateur-profil-fact__value">{displayName}</p>
                  </div>
                </li>
                <li className="formateur-profil-fact">
                  <span className="formateur-profil-fact__icon" aria-hidden>
                    <LucideSymbolIcon name="call" tone="inherit" size={18} />
                  </span>
                  <div className="min-w-0">
                    <p className="formateur-profil-fact__label">Téléphone</p>
                    <p className="formateur-profil-fact__value">{telephone?.trim() || 'Non renseigné'}</p>
                  </div>
                </li>
                <li className="formateur-profil-fact">
                  <span className="formateur-profil-fact__icon" aria-hidden>
                    <LucideSymbolIcon name="mail" tone="inherit" size={18} />
                  </span>
                  <div className="min-w-0">
                    <p className="formateur-profil-fact__label">E-mail</p>
                    <p className="formateur-profil-fact__value break-all">{email?.trim() || 'Non renseigné'}</p>
                  </div>
                </li>
              </ul>

              <div className="formateur-profil-docs">
                <p className="formateur-profil-docs__title">Documents soumis</p>
                {documents.length === 0 ? (
                  <p className="formateur-profil-empty-inline">Aucun document soumis.</p>
                ) : (
                  <ul className="formateur-profil-docs__list">
                    {documents.map((d) => (
                      <li key={d.id} className="formateur-profil-doc-row">
                        <span className="formateur-profil-doc-row__type">{d.typeDocument}</span>
                        <span
                          className={`formateur-profil-doc-row__badge formateur-profil-doc-row__badge--${docStatutTone(d.statutValidation)}`}
                        >
                          {formatDocStatut(d.statutValidation)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>

          <section
            className="formateur-profil-panel formateur-profil-panel--wide"
            aria-labelledby="formateur-profil-parcours-heading"
          >
            <header className="formateur-profil-panel__header">
              <span className="formateur-profil-panel__header-icon" aria-hidden>
                <LucideSymbolIcon name="school" tone="inherit" size={22} />
              </span>
              <div className="min-w-0">
                <h2 id="formateur-profil-parcours-heading" className="formateur-profil-panel__title">
                  Parcours &amp; expertise
                </h2>
                <p className="formateur-profil-panel__subtitle">
                  Spécialité, expérience et éléments visibles par l&apos;administration
                </p>
              </div>
            </header>
            <div className="formateur-profil-panel__body">
              <div className="formateur-profil-form">
                <label className="formateur-profil-form__field formateur-profil-form__field--wide">
                  <span className="formateur-profil-form__label">Spécialité *</span>
                  <AcademiaSelect
                    className="formateur-profil-form__input w-full"
                    value={form.specialite}
                    onChange={(e) => setForm((f) => ({ ...f, specialite: e.target.value }))}
                    required
                    aria-label="Spécialité"
                  >
                    <option value="" disabled>
                      Choisir une spécialité
                    </option>
                    {specialiteOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </AcademiaSelect>
                </label>
                <label className="formateur-profil-form__field">
                  <span className="formateur-profil-form__label">Années d&apos;expérience *</span>
                  <input
                    type="number"
                    min={0}
                    className="formateur-profil-form__input"
                    value={form.experienceAnnees}
                    onChange={(e) => setForm((f) => ({ ...f, experienceAnnees: e.target.value }))}
                  />
                </label>
                <label className="formateur-profil-form__field">
                  <span className="formateur-profil-form__label">CV (PDF)</span>
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    className="formateur-profil-form__input formateur-profil-form__input--file"
                    onChange={(e) => setCvFile(e.target.files?.[0] ?? null)}
                  />
                  {cvFile ? (
                    <p className="formateur-profil-form__hint">Nouveau CV : {cvFile.name}</p>
                  ) : form.urlCv ? (
                    <p className="formateur-profil-form__hint">CV actuel conservé.</p>
                  ) : (
                    <p className="formateur-profil-form__hint">Aucun CV enregistré.</p>
                  )}
                  {form.urlCv ? (
                    <a
                      href={form.urlCv}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="formateur-profil-cv__link"
                    >
                      <LucideSymbolIcon name="link" tone="inherit" size={14} />
                      Voir le CV en ligne
                    </a>
                  ) : null}
                </label>
                <label className="formateur-profil-form__field formateur-profil-form__field--wide">
                  <span className="formateur-profil-form__label">Biographie *</span>
                  <textarea
                    className="formateur-profil-form__input formateur-profil-form__textarea"
                    rows={5}
                    placeholder="Présentez votre parcours, vos domaines d'intervention et votre approche pédagogique…"
                    value={form.biographie}
                    onChange={(e) => setForm((f) => ({ ...f, biographie: e.target.value }))}
                  />
                </label>
              </div>

              <div className="formateur-profil-panel__actions">
                <button
                  type="button"
                  disabled={isSaving || isUploadingPhoto}
                  onClick={() => void handleSave()}
                  className="formateur-profil__btn formateur-profil__btn--primary"
                >
                  <LucideSymbolIcon name="save" tone="inherit" size={16} className="text-white" />
                  {isSaving ? 'Enregistrement…' : 'Enregistrer le profil'}
                </button>
                {!onboarding ? (
                  <button
                    type="button"
                    onClick={() => void loadProfil()}
                    className="formateur-profil__btn formateur-profil__btn--outline"
                  >
                    Annuler les modifications
                  </button>
                ) : null}
              </div>

              <div className="formateur-profil-extras">
                <FormateurProfilExtrasEditor
                  diplomes={diplomes}
                  disponibilites={disponibilites}
                  referencements={referencements}
                  onDiplomesChange={setDiplomes}
                  onDisponibilitesChange={setDisponibilites}
                  onReferencementsChange={setReferencements}
                />
              </div>
            </div>
          </section>
        </div>

        <section className="formateur-profil-panel" aria-labelledby="formateur-profil-eval-heading">
          <header className="formateur-profil-panel__header">
            <span className="formateur-profil-panel__header-icon formateur-profil-panel__header-icon--sky" aria-hidden>
              <LucideSymbolIcon name="star" tone="inherit" size={22} />
            </span>
            <div className="min-w-0 flex-1">
              <h2 id="formateur-profil-eval-heading" className="formateur-profil-panel__title">
                Évaluations &amp; indicateurs
              </h2>
              <p className="formateur-profil-panel__subtitle">
                Notes apprenants et suivi de la saisie des présences sur vos séances
              </p>
            </div>
          </header>
          <div className="formateur-profil-panel__body">
            {perf ? (
              <div className="formateur-profil-kpi">
                <article className="formateur-profil-kpi__card">
                  <p className="formateur-profil-kpi__label">Ponctualité</p>
                  <p className="formateur-profil-kpi__value">
                    {perf.notePonctualiteMoyenne != null ? `${perf.notePonctualiteMoyenne.toFixed(1)}/5` : '—'}
                  </p>
                </article>
                <article className="formateur-profil-kpi__card">
                  <p className="formateur-profil-kpi__label">Pédagogie</p>
                  <p className="formateur-profil-kpi__value">
                    {perf.notePedagogieMoyenne != null ? `${perf.notePedagogieMoyenne.toFixed(1)}/5` : '—'}
                  </p>
                </article>
                <article className="formateur-profil-kpi__card">
                  <p className="formateur-profil-kpi__label">Satisfaction</p>
                  <p className="formateur-profil-kpi__value">
                    {perf.tauxSatisfactionPct != null ? `${perf.tauxSatisfactionPct}%` : '—'}
                  </p>
                </article>
                {indicateurs ? (
                  <>
                    <article className="formateur-profil-kpi__card">
                      <p className="formateur-profil-kpi__label">Saisie présences</p>
                      <p className="formateur-profil-kpi__value">
                        {indicateurs.tauxSaisiePresencesPct != null
                          ? `${indicateurs.tauxSaisiePresencesPct}%`
                          : '—'}
                      </p>
                      <p className="formateur-profil-kpi__hint">
                        {indicateurs.seancesAvecPresencesSaisies}/{indicateurs.seancesPassees} séances
                      </p>
                    </article>
                    <article className="formateur-profil-kpi__card">
                      <p className="formateur-profil-kpi__label">Prestations</p>
                      <p className="formateur-profil-kpi__value">
                        {indicateurs.nombrePrestations ?? 0}
                      </p>
                      <p className="formateur-profil-kpi__hint">
                        {indicateurs.heuresPresteesTotal != null && indicateurs.heuresPresteesTotal > 0
                          ? `${indicateurs.heuresPresteesTotal} h prestées`
                          : 'Vacations enregistrées après saisie des présences'}
                      </p>
                    </article>
                  </>
                ) : null}
              </div>
            ) : null}

            {evaluations.length > 0 ? (
              <ul className="formateur-profil-evals">
                {evaluations.map((ev) => (
                  <li key={ev.id} className="formateur-profil-eval">
                    <div className="min-w-0 flex-1">
                      <p className="formateur-profil-eval__name">{ev.apprenantLabel ?? 'Apprenant'}</p>
                      <p className="formateur-profil-eval__meta">
                        {ev.cohorteCode ? `Cohorte ${ev.cohorteCode} · ` : ''}
                        {new Date(ev.dateCreation).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </p>
                      {ev.commentaire ? (
                        <p className="formateur-profil-eval__comment">{ev.commentaire}</p>
                      ) : null}
                    </div>
                    <div className="formateur-profil-eval__scores">
                      <div className="formateur-profil-eval__score">
                        <span className="formateur-profil-eval__score-label">Ponctualité</span>
                        <span className="formateur-profil-eval__score-value">{ev.notePonctualite}/5</span>
                      </div>
                      <div className="formateur-profil-eval__score">
                        <span className="formateur-profil-eval__score-label">Pédagogie</span>
                        <span className="formateur-profil-eval__score-value">{ev.notePedagogie}/5</span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="formateur-profil-empty">
                <LucideSymbolIcon name="star" tone="primary" size={36} className="opacity-70" />
                <p>Aucune évaluation apprenant pour le moment.</p>
                <p className="text-xs">Les notes apparaîtront après les cours évalués.</p>
              </div>
            )}
          </div>
        </section>

        <section className="formateur-profil-panel" aria-labelledby="formateur-profil-security-heading">
          <header className="formateur-profil-panel__header">
            <span className="formateur-profil-panel__header-icon formateur-profil-panel__header-icon--security" aria-hidden>
              <LucideSymbolIcon name="lock" tone="inherit" size={22} />
            </span>
            <div className="min-w-0">
              <h2 id="formateur-profil-security-heading" className="formateur-profil-panel__title">
                Sécurité du compte
              </h2>
              <p className="formateur-profil-panel__subtitle">
                {passwordOnboarding
                  ? 'Remplacez le code provisoire par un mot de passe personnel (8 caractères minimum).'
                  : 'Modifiez votre mot de passe de connexion à tout moment.'}
              </p>
            </div>
          </header>
          <div className="formateur-profil-panel__body">
            <div className="formateur-profil-form formateur-profil-form--security">
              <label className="formateur-profil-form__field">
                <span className="formateur-profil-form__label">
                  {passwordOnboarding ? 'Code provisoire (e-mail)' : 'Mot de passe actuel'}
                </span>
                <input
                  type="password"
                  value={ancienMotdepasse}
                  onChange={(e) => setAncienMotdepasse(e.target.value)}
                  className="formateur-profil-form__input"
                  placeholder={passwordOnboarding ? 'Code à 6 chiffres' : undefined}
                  autoComplete="current-password"
                />
              </label>
              <label className="formateur-profil-form__field">
                <span className="formateur-profil-form__label">Nouveau mot de passe</span>
                <input
                  type="password"
                  value={nouveauMotdepasse}
                  onChange={(e) => setNouveauMotdepasse(e.target.value)}
                  className="formateur-profil-form__input"
                  placeholder="8 caractères minimum"
                  autoComplete="new-password"
                />
              </label>
              <label className="formateur-profil-form__field">
                <span className="formateur-profil-form__label">Confirmer le nouveau mot de passe</span>
                <input
                  type="password"
                  value={confirmMotdepasse}
                  onChange={(e) => setConfirmMotdepasse(e.target.value)}
                  className="formateur-profil-form__input"
                  autoComplete="new-password"
                />
              </label>
            </div>
            <div className="formateur-profil-panel__actions">
              <button
                type="button"
                onClick={() => void handleChangePassword()}
                disabled={isChangingPassword}
                className="formateur-profil__btn formateur-profil__btn--primary"
              >
                <LucideSymbolIcon name="lock" tone="inherit" size={16} className="text-white" />
                {isChangingPassword ? 'Mise à jour…' : 'Changer le mot de passe'}
              </button>
            </div>
          </div>
        </section>
      </div>
    </FormateurPageShell>
  );
}
